import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Info, Loader2, CalendarClock, FileText, FileDown, TrendingUp, TrendingDown, Minus, Plus, Trash2, ShieldCheck, User } from "lucide-react";
import { fmtEUR } from "@/lib/format";
import { toast } from "sonner";
import UpgradeCard from "@/components/UpgradeCard";

const API = process.env.REACT_APP_BACKEND_URL;
const SOURCE_LABEL = { manual:"Manuell", manual_enriched:"Manuell + Broker", okx:"OKX", bitget:"Bitget", binance:"Binance", kraken:"Kraken", bitunix:"Bitunix", levex:"LeveX" };
const todayIso = () => new Date().toISOString().slice(0, 10);
const nowYear = new Date().getFullYear();
const YEARS = [nowYear, nowYear - 1, nowYear - 2, nowYear - 3];
const FSA_MAX = { single: 1000, joint: 2000 };

export default function TaxTool() {
  const [year, setYear] = useState(String(nowYear));
  const [mode, setMode] = useState("annual");
  const [dateFrom, setDateFrom] = useState(`${nowYear}-01-01`);
  const [dateTo, setDateTo] = useState(todayIso());
  const [data, setData] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("bericht");
  const [locked, setLocked] = useState(false);
  const isCurrentYear = Number(year) === nowYear;

  useEffect(() => { if (!isCurrentYear && mode === "interim") setMode("annual"); }, [isCurrentYear, mode]);
  useEffect(() => { setDateFrom(`${year}-01-01`); setDateTo(isCurrentYear ? todayIso() : `${year}-12-31`); }, [year, isCurrentYear]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { year: Number(year) };
      if (mode === "interim") { params.interim = true; params.date_from = dateFrom; params.date_to = dateTo; }
      const [r, p] = await Promise.all([
        axios.get(`${API}/api/tax/report`, { params, withCredentials: true }),
        axios.get(`${API}/api/tax/profile`, { withCredentials: true }),
      ]);
      setData(r.data); setProfile(p.data); setLocked(false);
    } catch (e) {
      const status = e?.response?.status;
      const code = e?.response?.data?.detail?.code;
      if (status === 402 && code === "plan_upgrade_required") {
        setLocked(true);
      } else {
        toast.error("Bericht konnte nicht geladen werden.", { description: e?.response?.data?.detail?.message || e?.response?.data?.detail || "" });
      }
    } finally { setLoading(false); }
  }, [year, mode, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const exportPdf = async () => {
    if (!data) return;
    try {
      const params = new URLSearchParams({ year: String(data.year) });
      if (data.mode === "interim") { params.set("interim", "true"); params.set("date_from", data.range.from); params.set("date_to", data.range.to); }
      const res = await axios.get(`${API}/api/tax/report.pdf?${params}`, { responseType: "blob", withCredentials: true });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `steuerbericht_${data.year}_${data.mode === "interim" ? `${data.range.from}_bis_${data.range.to}` : "jahresabschluss"}.pdf`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (e) { toast.error("PDF-Export fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Steuer-Tool" title="Steuer-Tool"
        description="Deutscher Steuerbericht nach § 23 EStG und § 20 Abs. 2 EStG mit Freistellungsauftrag, Verlustverrechnung und Freigrenze."
        testid="tax-header"
        actions={locked ? null : (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-28 h-11" data-testid="tax-year"><SelectValue /></SelectTrigger>
              <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="rounded-full h-11" onClick={exportPdf} disabled={!data || loading} data-testid="tax-export-pdf">
              <FileDown className="mr-2 h-4 w-4" strokeWidth={1.5} /> PDF herunterladen
            </Button>
          </div>
        )}
      />

      {locked && (
        <UpgradeCard
          feature="Das Steuer-Tool"
          benefit="Erhalte einen prüffähigen PDF-Bericht nach § 23 EStG (Spot) und § 20 Abs. 2 EStG (Derivate) inklusive Freistellungsauftrag, Verlustverrechnung und Vorjahresvergleich — direkt aus deinen Trades erzeugt."
          testid="tax-upgrade-card"
        />
      )}

      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList data-testid="tax-tabs">
          <TabsTrigger value="bericht" data-testid="tax-tab-bericht">Bericht</TabsTrigger>
          <TabsTrigger value="profil" data-testid="tax-tab-profil">Meine Angaben</TabsTrigger>
          <TabsTrigger value="fsa" data-testid="tax-tab-fsa">Freistellungsaufträge</TabsTrigger>
        </TabsList>

        <TabsContent value="bericht" className="mt-6">
          {/* Modus */}
          <div className="card-surface p-5 mb-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div>
                <div className="overline">Berichtsart</div>
                <div className="mt-2 inline-flex rounded-full border border-border bg-secondary/50 p-1">
                  <button type="button" onClick={() => setMode("annual")} className={`h-9 px-4 rounded-full text-sm transition-colors ${mode === "annual" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="tax-mode-annual">
                    <FileText className="inline mr-2 h-4 w-4 -mt-0.5" strokeWidth={1.5} />Jahresabschluss
                  </button>
                  <button type="button" onClick={() => setMode("interim")} disabled={!isCurrentYear} className={`h-9 px-4 rounded-full text-sm transition-colors ${mode === "interim" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"} ${!isCurrentYear ? "opacity-40 cursor-not-allowed" : ""}`} data-testid="tax-mode-interim">
                    <CalendarClock className="inline mr-2 h-4 w-4 -mt-0.5" strokeWidth={1.5} />Zwischenbericht
                  </button>
                </div>
              </div>
              {mode === "interim" && isCurrentYear && (
                <div className="flex items-end gap-3 flex-wrap">
                  <div><Label className="text-xs uppercase text-muted-foreground">Von</Label><Input type="date" value={dateFrom} min={`${year}-01-01`} max={dateTo} onChange={(e) => setDateFrom(e.target.value)} className="mt-1.5 h-11 w-44 font-mono" data-testid="tax-date-from"/></div>
                  <div><Label className="text-xs uppercase text-muted-foreground">Bis</Label><Input type="date" value={dateTo} min={dateFrom} max={`${year}-12-31`} onChange={(e) => setDateTo(e.target.value)} className="mt-1.5 h-11 w-44 font-mono" data-testid="tax-date-to"/></div>
                </div>
              )}
            </div>
          </div>

          {loading ? <div className="card-surface p-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" strokeWidth={1.5} /></div> : data && <BerichtView data={data} />}
        </TabsContent>

        <TabsContent value="profil" className="mt-6">
          <ProfileEditor profile={profile} onSaved={(p) => { setProfile(p); load(); }} />
        </TabsContent>

        <TabsContent value="fsa" className="mt-6">
          <FsaEditor profile={profile} onSaved={(p) => { setProfile(p); load(); }} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BerichtView({ data }) {
  const p23 = data.para_23_short, p23L = data.para_23_long, p20 = data.para_20;
  return (
    <>
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Steuerpflichtig gesamt" value={fmtEUR(data.steuerpflichtig_total, {signed:true})} highlight testid="kpi-tax-total" />
        <Kpi label="§ 23 nach Freigrenze" value={fmtEUR(p23.steuerpflichtig, {signed:true})} hint={p23.freigrenze_greift ? "Freigrenze 1.000 € greift" : ""} testid="kpi-p23" />
        <Kpi label="§ 20 nach Cap + FSA" value={fmtEUR(p20.netto_nach_fsa, {signed:true})} hint={`FSA verbraucht: ${fmtEUR(p20.fsa_verbraucht || 0)}`} testid="kpi-p20" />
        <Kpi label="Steuerfrei (§ 23 >1J)" value={fmtEUR(p23L.netto, {signed:true})} hint={`${p23L.trades} Trade(s)`} testid="kpi-taxfree" />
      </section>

      {data.previous_year && (
        <div className="card-surface p-5 mb-6" data-testid="tax-compare">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="overline flex items-center gap-2">
                {data.previous_year.direction === "better" ? <TrendingUp className="h-3.5 w-3.5 text-[hsl(122_39%_65%)]" strokeWidth={1.5} /> : data.previous_year.direction === "worse" ? <TrendingDown className="h-3.5 w-3.5 text-[hsl(0_66%_72%)]" strokeWidth={1.5} /> : <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />}
                Vorjahresvergleich (gleicher Zeitraum)
              </div>
              <div className="mt-2 flex items-baseline gap-3 flex-wrap font-mono text-sm">
                <span className="text-lg">{data.year}: <span className={data.steuerpflichtig_total >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}>{fmtEUR(data.steuerpflichtig_total, {signed:true})}</span></span>
                <span className="text-muted-foreground">vs.</span>
                <span className="text-muted-foreground">{data.year - 1}: {fmtEUR(data.previous_year.steuerpflichtig_total, {signed:true})}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="overline">Differenz</div>
              <div className={`font-mono text-2xl mt-1 ${data.previous_year.delta_abs >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>{fmtEUR(data.previous_year.delta_abs, {signed:true})}</div>
              {data.previous_year.delta_pct !== null && <div className="text-xs text-muted-foreground">{data.previous_year.delta_pct > 0 ? "+" : ""}{data.previous_year.delta_pct}%</div>}
            </div>
          </div>
        </div>
      )}

      <div className="card-surface p-4 md:p-6 mb-6">
        <h2 className="font-heading text-lg mb-1">Steuer-Kategorien</h2>
        <p className="text-xs text-muted-foreground mb-4">Gewinne/Verluste jeweils <b>brutto</b> (vor Gebühren). Netto = Brutto − Gebühren.</p>
        <Table data-testid="tax-cat-table">
          <TableHeader><TableRow><TableHead>Kategorie</TableHead><TableHead className="text-right">Gewinne (brutto)</TableHead><TableHead className="text-right">Verluste (brutto)</TableHead><TableHead className="text-right">Gebühren</TableHead><TableHead className="text-right">Netto</TableHead><TableHead className="text-right">Trades</TableHead></TableRow></TableHeader>
          <TableBody>
            <TableRow data-testid="cat-para23"><TableCell><div className="font-medium">§ 23 EStG</div><div className="text-xs text-muted-foreground">Krypto Spot &lt; 1 Jahr</div></TableCell><TableCell className="text-right font-mono">{fmtEUR(p23.gewinne_brutto)}</TableCell><TableCell className="text-right font-mono text-[hsl(0_66%_72%)]">{fmtEUR(p23.verluste_brutto)}</TableCell><TableCell className="text-right font-mono text-muted-foreground">{fmtEUR(p23.fees)}</TableCell><TableCell className={`text-right font-mono ${p23.netto >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>{fmtEUR(p23.netto, {signed:true})}</TableCell><TableCell className="text-right font-mono">{p23.trades}</TableCell></TableRow>
            {p23.freigrenze_greift && <TableRow className="bg-[hsl(122_39%_49%/0.06)]"><TableCell className="text-xs italic pl-6">Freigrenze 1.000 € greift → 0 € steuerpflichtig</TableCell><TableCell colSpan={3}/><TableCell className="text-right font-mono">{fmtEUR(0)}</TableCell><TableCell/></TableRow>}
            <TableRow data-testid="cat-para23-long"><TableCell><div className="font-medium">§ 23 EStG steuerfrei</div><div className="text-xs text-muted-foreground">Krypto Spot ≥ 1 Jahr</div></TableCell><TableCell className="text-right font-mono">{fmtEUR(p23L.gewinne_brutto)}</TableCell><TableCell className="text-right font-mono text-[hsl(0_66%_72%)]">{fmtEUR(p23L.verluste_brutto)}</TableCell><TableCell className="text-right font-mono text-muted-foreground">{fmtEUR(p23L.fees)}</TableCell><TableCell className="text-right font-mono text-muted-foreground italic">{fmtEUR(p23L.netto, {signed:true})}</TableCell><TableCell className="text-right font-mono">{p23L.trades}</TableCell></TableRow>
            <TableRow data-testid="cat-para20"><TableCell><div className="font-medium">§ 20 Abs. 2 EStG</div><div className="text-xs text-muted-foreground">Termingeschäfte / Futures</div></TableCell><TableCell className="text-right font-mono">{fmtEUR(p20.gewinne_brutto)}</TableCell><TableCell className="text-right font-mono text-[hsl(0_66%_72%)]">{fmtEUR(p20.verluste_brutto)}</TableCell><TableCell className="text-right font-mono text-muted-foreground">{fmtEUR(p20.fees)}</TableCell><TableCell className={`text-right font-mono ${p20.netto >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>{fmtEUR(p20.netto, {signed:true})}</TableCell><TableCell className="text-right font-mono">{p20.trades}</TableCell></TableRow>
            {p20.verluste_ueberhang !== 0 && <TableRow className="bg-[hsl(43_74%_49%/0.08)]"><TableCell className="text-xs italic pl-6">Verlust-Überhang &gt; 20.000 € Cap → Vortrag ins Folgejahr</TableCell><TableCell colSpan={3}/><TableCell className="text-right font-mono text-[hsl(43_74%_66%)]">{fmtEUR(p20.verluste_ueberhang)}</TableCell><TableCell/></TableRow>}
            {(p20.fsa_verbraucht > 0) && <TableRow className="bg-[hsl(122_39%_49%/0.06)]"><TableCell className="text-xs italic pl-6">abzgl. Freistellungsauftrag (verbraucht {fmtEUR(p20.fsa_verbraucht)})</TableCell><TableCell colSpan={3}/><TableCell className="text-right font-mono">{fmtEUR(p20.netto_nach_fsa, {signed:true})}</TableCell><TableCell/></TableRow>}
            <TableRow className="border-t-2 border-primary/40 bg-primary/5"><TableCell className="font-bold">Steuerpflichtig gesamt</TableCell><TableCell colSpan={3}/><TableCell className="text-right font-mono font-bold">{fmtEUR(data.steuerpflichtig_total, {signed:true})}</TableCell><TableCell/></TableRow>
          </TableBody>
        </Table>
      </div>

      <div className="card-surface p-4 md:p-6">
        <h2 className="font-heading text-lg mb-4">Aufschlüsselung nach Handelsplatz</h2>
        <Table><TableHeader><TableRow><TableHead>Quelle</TableHead><TableHead className="text-right">Realisiert</TableHead><TableHead className="text-right">Gebühren</TableHead><TableHead className="text-right">Trades</TableHead></TableRow></TableHeader>
          <TableBody>
            {Object.keys(data.per_source).length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground italic py-6">Keine Trades im Zeitraum</TableCell></TableRow> :
              Object.entries(data.per_source).sort().map(([src, v]) => <TableRow key={src}><TableCell><Badge variant="secondary" className="rounded-full">{SOURCE_LABEL[src] || src}</Badge></TableCell><TableCell className={`text-right font-mono ${v.realized >= 0 ? "text-[hsl(122_39%_65%)]" : "text-[hsl(0_66%_72%)]"}`}>{fmtEUR(v.realized, {signed:true})}</TableCell><TableCell className="text-right font-mono text-muted-foreground">{fmtEUR(v.fees)}</TableCell><TableCell className="text-right font-mono">{v.trades}</TableCell></TableRow>)}
          </TableBody>
        </Table>
        <p className="mt-6 text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-4">{data.notes.disclaimer}</p>
      </div>
    </>
  );
}

function ProfileEditor({ profile, onSaved }) {
  const [f, setF] = useState(profile || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => setF(profile || {}), [profile]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/api/tax/profile`, { ...f, freistellungsauftraege: profile?.freistellungsauftraege || [] }, { withCredentials: true });
      onSaved(res.data);
      toast.success("Angaben gespeichert");
    } catch (e) { toast.error("Speichern fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-surface p-6 max-w-3xl" data-testid="tax-profile-editor">
      <div className="flex items-center gap-2 mb-4"><User className="h-5 w-5 text-primary" strokeWidth={1.5} /><h2 className="font-heading text-lg">Persönliche Angaben für den Steuerbericht</h2></div>
      <p className="text-sm text-muted-foreground mb-6">Diese Angaben erscheinen im PDF-Bericht als Deckblatt. Der Steuerberater braucht sie für die Zuordnung.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldR label="Vollständiger Name"><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="tp-name" /></FieldR>
        <FieldR label="Steuer-Identifikationsnummer"><Input value={f.steuer_id || ""} onChange={(e) => set("steuer_id", e.target.value)} placeholder="11-stellig" data-testid="tp-steuerid" /></FieldR>
        <FieldR label="Steuernummer"><Input value={f.steuernummer || ""} onChange={(e) => set("steuernummer", e.target.value)} placeholder="z. B. 12/345/67890" data-testid="tp-steuernummer" /></FieldR>
        <FieldR label="Finanzamt"><Input value={f.finanzamt || ""} onChange={(e) => set("finanzamt", e.target.value)} placeholder="z. B. Berlin-Mitte" data-testid="tp-finanzamt" /></FieldR>
        <FieldR label="Straße & Hausnr."><Input value={f.address_street || ""} onChange={(e) => set("address_street", e.target.value)} data-testid="tp-street" /></FieldR>
        <div className="grid grid-cols-3 gap-3">
          <FieldR label="PLZ"><Input value={f.address_zip || ""} onChange={(e) => set("address_zip", e.target.value)} data-testid="tp-zip" /></FieldR>
          <div className="col-span-2"><FieldR label="Stadt"><Input value={f.address_city || ""} onChange={(e) => set("address_city", e.target.value)} data-testid="tp-city" /></FieldR></div>
        </div>
        <FieldR label="Veranlagung">
          <Select value={f.marital_status || "single"} onValueChange={(v) => set("marital_status", v)}>
            <SelectTrigger data-testid="tp-marital"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="single">Einzelveranlagung (1.000 € FSA)</SelectItem><SelectItem value="joint">Zusammenveranlagung (2.000 € FSA)</SelectItem></SelectContent>
          </Select>
        </FieldR>
      </div>
      <Button className="rounded-full h-11 px-6 mt-6" onClick={save} disabled={busy} data-testid="tp-save"><ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />{busy ? "Speichert…" : "Angaben speichern"}</Button>
    </div>
  );
}

function FsaEditor({ profile, onSaved }) {
  const [rows, setRows] = useState(profile?.freistellungsauftraege || []);
  const [busy, setBusy] = useState(false);
  useEffect(() => setRows(profile?.freistellungsauftraege || []), [profile]);

  const cap = FSA_MAX[profile?.marital_status || "single"];
  const perYear = useMemo(() => {
    const acc = {};
    rows.forEach((r) => { const y = r.year || nowYear; acc[y] = (acc[y] || 0) + Number(r.amount || 0); });
    return acc;
  }, [rows]);

  const add = () => setRows([...rows, { broker: "", amount: 0, year: nowYear }]);
  const upd = (i, k, v) => {
    const c = [...rows];
    let val = v;
    if (k === "amount") {
      // Leer erlauben (bleibt undefined bis User tippt), sonst als Zahl (0 gültig)
      val = v === "" ? "" : Number(v);
    } else if (k === "year") {
      val = Number(v);
    }
    c[i] = { ...c[i], [k]: val };
    setRows(c);
  };
  const del = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/api/tax/profile`, { ...profile, freistellungsauftraege: rows }, { withCredentials: true });
      onSaved(res.data);
      toast.success("Freistellungsaufträge gespeichert");
    } catch (e) { toast.error("Speichern fehlgeschlagen", { description: e?.response?.data?.detail || "" }); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-surface p-6" data-testid="tax-fsa-editor">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div><h2 className="font-heading text-lg">Freistellungsaufträge</h2><p className="text-sm text-muted-foreground mt-1">Sparerpauschbetrag pro Börse/Anbieter. Höchstbetrag {profile?.marital_status === "joint" ? "2.000 € (Zusammenveranlagung)" : "1.000 € (Einzelveranlagung)"} pro Jahr insgesamt. <span className="italic">0 € eintragen, wenn der Auftrag anderweitig verbraucht ist (z. B. bereits bei einer Bank für Zinsen).</span></p></div>
        <Button variant="outline" size="sm" onClick={add} className="rounded-full" data-testid="fsa-add"><Plus className="mr-2 h-4 w-4" strokeWidth={1.5} />Broker hinzufügen</Button>
      </div>
      {rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-8 text-center text-sm text-muted-foreground" data-testid="fsa-empty">Noch keine Freistellungsaufträge angelegt. Klick „Broker hinzufügen".</div>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="space-y-1" data-testid={`fsa-row-${i}`}>
              <div className="grid grid-cols-12 gap-2 items-center">
                <Input className="col-span-5" placeholder="Broker/Anbieter (z. B. OKX, ING)" value={r.broker} onChange={(e) => upd(i, "broker", e.target.value)} data-testid={`fsa-broker-${i}`} />
                <div className="col-span-3 relative">
                  <Input type="number" min="0" max="2000" step="1" placeholder="Betrag (0 möglich)" value={r.amount === undefined || r.amount === null ? "" : r.amount} onChange={(e) => upd(i, "amount", e.target.value)} className="pr-8 font-mono" data-testid={`fsa-amount-${i}`} />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">€</span>
                </div>
                <div className="col-span-3"><Select value={String(r.year || nowYear)} onValueChange={(v) => upd(i, "year", v)}><SelectTrigger data-testid={`fsa-year-${i}`}><SelectValue /></SelectTrigger><SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select></div>
                <Button variant="ghost" size="icon" onClick={() => del(i)} className="col-span-1 text-[hsl(0_66%_72%)]" data-testid={`fsa-del-${i}`}><Trash2 className="h-4 w-4" strokeWidth={1.5} /></Button>
              </div>
              {Number(r.amount) === 0 && r.amount !== "" && (
                <div className="pl-1 text-[11px] text-muted-foreground flex items-center gap-1.5 leading-snug" data-testid={`fsa-zero-note-${i}`}>
                  <Info className="h-3 w-3 shrink-0 opacity-70" strokeWidth={1.5} />
                  <span>Kein Sparerpauschbetrag bei diesem Broker anrechenbar — vermutlich anderweitig verbraucht.</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap gap-2 text-xs">
        {Object.entries(perYear).map(([y, sum]) => (
          <Badge key={y} variant="secondary" className={`rounded-full ${sum > cap ? "bg-[hsl(0_66%_50%/0.15)] text-[hsl(0_66%_72%)] border border-[hsl(0_66%_50%/0.3)]" : ""}`} data-testid={`fsa-sum-${y}`}>
            {y}: {fmtEUR(sum)} / {fmtEUR(cap)}{sum > cap && " · übersteigt Höchstbetrag!"}
          </Badge>
        ))}
      </div>
      <Button className="rounded-full h-11 px-6 mt-6" onClick={save} disabled={busy} data-testid="fsa-save"><ShieldCheck className="mr-2 h-4 w-4" strokeWidth={1.5} />{busy ? "Speichert…" : "Freistellungsaufträge speichern"}</Button>
      <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">Hinweis: Freistellungsaufträge gelten nur bei inländischen Banken/Brokern, die Kapitalertragsteuer direkt abführen. Bei ausländischen Krypto-Börsen sind Erträge in der Anlage KAP / SO deiner Einkommensteuererklärung zu erklären; der Freistellungsauftrag wirkt dann rechnerisch als Sparerpauschbetrag.</p>
    </div>
  );
}

function Kpi({ label, value, hint, highlight, testid }) {
  return (
    <div className={`card-surface p-5 ${highlight ? "border-primary/40 bg-primary/5" : ""}`} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-mono text-2xl mt-2">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

function FieldR({ label, children }) {
  return <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label><div className="mt-1.5">{children}</div></div>;
}
